import React, { FC } from "react";
import { useSelector } from "react-redux";
import classnames from "classnames";
import { getGbFormattedShortDate } from "@shared/utils";

import { BadgeCardViewModel } from "../buildBadgeCards";
import { pointsToNextCaption } from "../pointsToNextCaption";
import { resolveBadgeIcon } from "../badgeArt";
import MysteryBadgeArt from "../MysteryBadgeArt/MysteryBadgeArt";
// Relative, not "@containers/Recognition/store": a real (non-type-only) selector import, and jest
// resolves no path aliases here — same reasoning as every other direct import on this shelf. Reached
// straight into `store/selectors`, never through the `store` barrel, for the same reason
// BadgeOverlayDetailsContainer.tsx's own imports give (a barrel import would drag reducer/saga/actions
// into every suite that mounts a single badge tile).
import { getCoreValues } from "../../../store/selectors";

import "./index.scss";

interface BadgeCardProps {
  badge: BadgeCardViewModel;
  onBadgeClick: (badge: BadgeCardViewModel) => void;
}

/**
 * The caption line (overview-rebuild-pr3-plan.md "The caption line"): an earned card shows its award
 * date; a locked card that is its ladder's own next tier shows distance-to-go (shared with
 * `BadgeOverlayDetailsContainer` via `pointsToNextCaption`); every other locked card shows the
 * definition's own `description` — the badge's criterion. `description` is `string | null`
 * (`shared/models/Badge.tsx`); `null` renders no caption element at all, not an empty one.
 */
const badgeCardCaption = (badge: BadgeCardViewModel): string | null => {
  if (badge.earned) {
    return getGbFormattedShortDate(badge.awardedAt);
  }
  if (badge.pointsToNext !== null) {
    return pointsToNextCaption(badge.pointsToNext);
  }
  return badge.description;
};

/**
 * One badge tile in the flat grid (overview-rebuild-pr3-plan.md): earned badges render in full
 * colour, locked badges dashed-bordered and dimmed (`--earned`/`--locked`). No progress bar and no
 * rarity stripe — both retired by this PR ("progressPercent -> pointsToNext; the bars go" /
 * "Rarity: the border goes, the field stays"); `rarity` itself stays on `BadgeCardViewModel` because
 * `BadgeOverlayDetailsContainer` still reads and renders it as text. Icon rendering matches the
 * remote-artwork precedent (`CoreValueCard.tsx`, `RecognitionOverlayDetailsContainer.tsx`): a raw
 * `backgroundImage` div, not an `Icon` glyph.
 *
 * A real `<button>`, not a `<div onClick>` — the same correction `RecognitionsHeader`'s back arrow and
 * parent crumb each got. As a div this tile had no role, no tab stop and no accessible name, and every
 * one of them opens the detail overlay that carries the badge's full description. That was a
 * ten-tile preview on the Overview; on the dedicated Badges screen all 84 tiles are the screen's
 * primary interaction, so the whole route was unreachable without a mouse.
 *
 * Per-value, flame, tenure and achievement badges have `icon_url: null` from the api by design — that
 * artwork is bundled with the app, not served (`badgeArt.ts`) — so `resolveBadgeIcon` reaches for the
 * bundled asset only when the api gave no `iconUrl` at all. A real `iconUrl` still wins outright, and the
 * resolver returns `null` for any code it has no art for. This is the ONE component
 * that reads redux directly on this otherwise-presentational shelf, scoped to exactly this lookup —
 * `coreValues` is small, slow-changing catalog data, and only per-value codes need it. Every screen that
 * renders this tile fetches it on mount: My Sparks (`BadgesSection`), All Badges (`BadgeCategoryCard`)
 * and a colleague's profile (`BadgeStrip`, `UserRecognitionsContainer`). A new screen that renders it
 * must do the same, or per-value tiles render without art.
 *
 * An EARNED tile has no `aria-label`: the accessible name comes from its own visible text, the real
 * name plus its caption (the award date). That is strictly more than a label would give, and it cannot
 * drift out of step with what is drawn.
 *
 * A LOCKED badge never shows its real name or its real, full-colour art (pavel's decision) — not even
 * for a card that is its own ladder's next tier. Its icon is `MysteryBadgeArt`'s faded silhouette, and
 * its title slot shows no name at all: the how-to-earn caption (the next tier's "N Sparks to go", or
 * the definition's own criterion) moves up into that slot instead, reusing the name's own typography
 * (`.badge-card-name`, already muted and 2-line-clamped for a locked card) rather than a separate
 * one-line caption. Because the visible text is now the caption alone, never a name, the tile carries
 * an explicit `aria-label` ("Locked badge: <caption>", or plain "Locked badge" when there is no
 * caption at all) so its accessible name still says the badge is locked.
 */
const BadgeCard: FC<BadgeCardProps> = (props) => {
  const { badge, onBadgeClick } = props;
  const { name, iconUrl, earned, code } = badge;
  const caption = badgeCardCaption(badge);
  const coreValues = useSelector(getCoreValues());
  // Served icon first, then bundled art, then none — the rule lives in `resolveBadgeIcon` so this tile
  // and its detail overlay can't drift apart.
  const resolvedIconUrl = resolveBadgeIcon(iconUrl, code, coreValues);
  const lockedAccessibleName = caption !== null ? `Locked badge: ${caption}` : "Locked badge";

  return (
    <button
      type="button"
      className={classnames("badge-card", earned ? "badge-card--earned" : "badge-card--locked")}
      aria-label={earned ? undefined : lockedAccessibleName}
      onClick={() => onBadgeClick(badge)}
    >
      {earned ? (
        <div
          className="badge-card-icon"
          style={resolvedIconUrl ? { backgroundImage: `url("${resolvedIconUrl}")` } : undefined}
        />
      ) : (
        <MysteryBadgeArt iconUrl={resolvedIconUrl} className="badge-card-icon" />
      )}
      {earned ? (
        <>
          <div className="badge-card-name">{name}</div>
          {caption !== null ? <div className="badge-card-caption">{caption}</div> : null}
        </>
      ) : caption !== null ? (
        <div className="badge-card-name">{caption}</div>
      ) : null}
    </button>
  );
};

export default BadgeCard;
